import { Component } from '@verndale/core';
import { gsap, Expo } from 'gsap';
import { v4 as uuidv4 } from 'uuid';
import { keyCode, debounce } from '../../helpers';
import Dropdown from '../../components/dropdown';
import { unset } from 'lodash';

class Module extends Component {
  setupDefaults() {
    this.dom = {
      tabsContainer: this.el.querySelector('.mega-menu-integrated-tab__tabs'),
      tabs: [...this.el.querySelectorAll('.mega-menu-integrated-tab__tab')],
      panelsContainer: this.el.querySelector('.mega-menu-integrated-tab__panels'),
      panels: [...this.el.querySelectorAll('.mega-menu-integrated-tab__panel')],
      videos: document.querySelectorAll('video')
    };

    this.buildNavigation();
    this.calculateSizes();
  }

  addListeners() {
    window.addEventListener(
      'resize',
      debounce(this.calculateSizes.bind(this), 300)
    );

    window.addEventListener('load', this.calculateSizes());

    this.dom.tabs.forEach((tab, index) => {
      tab.index = index;
      tab.addEventListener('click', this.handleTabClick.bind(this));
      tab.addEventListener('keydown', this.handleKeyDown.bind(this));
    });
  }

  buildNavigation() {
    const options = this.dom.tabs
      .map((tab, index) => {
        const tabGuid = uuidv4();
        const panelGuid = uuidv4();
        const panel = this.dom.panels[index];

        tab.setAttribute('id', tabGuid);
        tab.setAttribute('aria-controls', panelGuid);

        if (panel) {
          panel.setAttribute('id', panelGuid);
          panel.setAttribute('aria-labelledby', tabGuid);
        }
        return `<option value="${panelGuid}">${tab.textContent}</option>`;
      })
      .join('');

    const dropdownGuid = uuidv4();
    const dropdownContainer = document.createElement('div');
    dropdownContainer.classList.add('mega-menu-integrated-tab__dropdown');

    const dropdownLabel = document.createElement('label');
    dropdownLabel.setAttribute('for', dropdownGuid);
    dropdownLabel.classList.add('label');
    dropdownLabel.innerHTML = this.dom.tabsContainer.getAttribute('aria-label');

    const dropdown = document.createElement('select');
    dropdown.id = dropdownGuid;
    dropdown.innerHTML = options;

    dropdownContainer.appendChild(dropdownLabel);
    dropdownContainer.appendChild(dropdown);
    this.dom.panelsContainer.parentNode.insertBefore(
      dropdownContainer,
      this.dom.panelsContainer
    );

    this.dom.dropdown = new Dropdown(dropdown);

    this.dom.dropdown.on('option', this.handleOptionChange.bind(this));
  }

  handleTabClick(e) {
    const panelId = e.currentTarget.getAttribute('aria-controls');
    this.dom.dropdown.setValue(panelId);
  }

  handleOptionChange(e) {
    const panelId = e.target.value;
    this.changePanel(panelId);
  }

  handleKeyDown(e) {
    const key = e.keyCode;
    const target = e.target;

    switch (key) {
      case keyCode.END:
        e.preventDefault();
        // Activate last tab
        this.dom.tabs[this.dom.tabs.length - 1].focus();
        this.changePanel(
          this.dom.tabs[this.dom.tabs.length - 1].getAttribute('aria-controls')
        );
        break;
      case keyCode.HOME:
        e.preventDefault();
        // Activate first tab
        this.dom.tabs[0].focus();
        this.changePanel(this.dom.tabs[0].getAttribute('aria-controls'));
        break;
      case keyCode.UP:
      case keyCode.LEFT:
        e.preventDefault();
        // Focus previous tab
        if (this.dom.tabs[target.index - 1]) {
          this.dom.tabs[target.index - 1].focus();
        } else {
          this.dom.tabs[this.dom.tabs.length - 1].focus();
        }
        break;
      case keyCode.DOWN:
      case keyCode.RIGHT:
        e.preventDefault();
        // Focus next tab
        if (this.dom.tabs[target.index + 1]) {
          this.dom.tabs[target.index + 1].focus();
        } else {
          this.dom.tabs[0].focus();
        }
        break;
    }
  }

  changePanel(panelId) {
    const currentTab = this.dom.tabs.find(
      tab => tab.getAttribute('aria-selected') === 'true'
    );
    const activeTab = this.dom.tabs.find(
      tab => tab.getAttribute('aria-controls') === panelId
    );
    const currentPanelId = currentTab.getAttribute('aria-controls');

    if (currentTab !== activeTab) {
      activeTab.setAttribute('aria-selected', 'true');
      activeTab.setAttribute('tabindex', 0);

      currentTab.setAttribute('aria-selected', 'false');
      currentTab.setAttribute('tabindex', 1);

      currentTab.classList.remove('selected');
      activeTab.classList.add('selected');
      this.calculateSizes();

      this.dom.panels.forEach(panel => {
        if (panel.id === panelId) {
          gsap.to(panel, {
            autoAlpha: 1,
            ease: Expo.easeOut,
            duration: 0.4
          });
        } else {
          gsap.to(panel, {
            autoAlpha: 0,
            ease: Expo.easeOut,
            duration: 0.4
          });
        }
      });
      this.dom.panels.forEach(panel => {
        if (panel.id === currentPanelId) {
          const panelIframe = panel.querySelector('iframe');
          if (panelIframe)
            panelIframe.setAttribute('src', panelIframe.getAttribute('src'));
        }
      });

      setTimeout(() => {
        this.dom.videos.forEach(video => {
          video.pause();
          //video.currentTime = 0;
        });
      }, 0.4);
    }
  }

  calculateSizes() {
    this.dom.panelsContainer.style.height = 'unset';
    const tabHeight = this.dom.tabsContainer.offsetHeight;
    let maxHeight = tabHeight;

    this.dom.panels.forEach(panel => {
      if (panel.offsetHeight >= maxHeight) {
        maxHeight = panel.offsetHeight;
      }
    });
    this.dom.panelsContainer.style['height'] = `${maxHeight}px`;
  }
}

export default Module;
